import {
  AppBar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tab,
  Tabs,
  TextField,
  Typography
} from "@mui/material";
import { DataGrid, GridColDef, GridRowParams } from "@mui/x-data-grid";
import { useState } from "react";
import { useAdminFetcherWithSwr, useAdminSender } from "../api";
import { ConfigItem, ConfigUpdateRequest } from "../generatedApi/configManagerApi";
import Template from "./Template";

interface ConfigManagerPageProps {
  title: string;
  configGroup: string;
  description?: string;
}

const ConfigManagerPage = ({ title, configGroup, description }: ConfigManagerPageProps) => {
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingConfig, setEditingConfig] = useState<ConfigItem | null>(null);
  const [editValue, setEditValue] = useState("");

  const {
    data: configs,
    isLoading,
    error,
    mutate
  } = useAdminFetcherWithSwr({
    apiName: "config_manager",
    serviceName: "default",
    operationId: "listConfigsConfigsConfigGroupGet",
    requestParameters: { configGroup }
  });

  const {
    sender: updateConfig,
    isLoading: updating,
    error: updateError
  } = useAdminSender({
    apiName: "config_manager",
    serviceName: "default",
    operationId: "updateConfigConfigsConfigGroupConfigPathPut"
  });

  const handleRowDoubleClick = (params: GridRowParams) => {
    const config = params.row as ConfigItem;
    setEditingConfig(config);
    setEditValue(config.value);
    setEditDialogOpen(true);
  };

  const handleSave = async () => {
    if (!editingConfig) return;

    const updateRequest: ConfigUpdateRequest = {
      value: editValue
    };

    try {
      await updateConfig({
        configGroup,
        configPath: editingConfig.config_path,
        requestBody: updateRequest
      });
      await mutate();
      setEditDialogOpen(false);
      setEditingConfig(null);
      setEditValue("");
    } catch (error) {
      console.error("Failed to update config:", error);
    }
  };

  const handleCancel = () => {
    setEditDialogOpen(false);
    setEditingConfig(null);
    setEditValue("");
  };

  const columns: GridColDef[] = [
    {
      field: "config_path",
      headerName: "キー",
      width: 400,
      sortable: true,
      valueGetter: (value, row) => `${row.config_group}.${row.config_path}`
    },
    {
      field: "value",
      headerName: "値",
      width: 400,
      sortable: false
    },
    {
      field: "updated_at",
      headerName: "更新日時",
      width: 200,
      sortable: true,
      valueFormatter: (params) => {
        return new Date(params * 1000).toLocaleString("ja-JP");
      }
    }
  ];

  const rows = configs?.items || [];

  return (
    <Template title={title}>
      <AppBar component="div" position="static" elevation={0} sx={{ zIndex: 0 }}>
        <Tabs value={"config"} textColor="inherit">
          <Tab label="設定一覧" value="config" />
        </Tabs>
      </AppBar>
      <Box component="main" sx={{ flex: 1, px: 2, bgcolor: "#eaeff1" }}>
        <Box sx={{ height: "100%", pt: 2 }}>
          {isLoading && (
            <Box display="flex" justifyContent="center" alignItems="center" height="50%">
              <CircularProgress />
            </Box>
          )}

          {error && (
            <Box display="flex" justifyContent="center" alignItems="center" height="50%">
              <Typography color="error">エラーが発生しました: {JSON.stringify(error)}</Typography>
            </Box>
          )}

          {!isLoading && !error && (
            <>
              <Typography variant="h6" sx={{ mb: 2 }}>
                {title} ({rows.length}件)
              </Typography>
              {description && (
                <Typography variant="body2" sx={{ mb: 1, color: "text.secondary" }}>
                  {description}
                </Typography>
              )}
              <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
                設定を編集するには行をダブルクリックしてください
              </Typography>
              <Box sx={{ height: "calc(100vh - 200px)", width: "100%" }}>
                <DataGrid
                  rows={rows}
                  columns={columns}
                  getRowId={(row) => row.config_path}
                  onRowDoubleClick={handleRowDoubleClick}
                  pageSizeOptions={[10, 25, 50]}
                  initialState={{
                    pagination: {
                      paginationModel: { pageSize: 25 }
                    },
                    sorting: {
                      sortModel: [{ field: "config_path", sort: "asc" }]
                    }
                  }}
                  sx={{
                    bgcolor: "background.paper",
                    "& .MuiDataGrid-row:hover": {
                      cursor: "pointer"
                    }
                  }}
                />
              </Box>
            </>
          )}
        </Box>
      </Box>

      <Dialog open={editDialogOpen} onClose={handleCancel} maxWidth="md" fullWidth>
        <DialogTitle>設定の編集</DialogTitle>
        <DialogContent>
          {editingConfig && (
            <>
              <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
                キー: {editingConfig.config_group}.{editingConfig.config_path}
              </Typography>
              <TextField
                label="値"
                multiline
                rows={4}
                fullWidth
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                sx={{ mt: 1 }}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel} disabled={updating}>
            キャンセル
          </Button>
          <Button onClick={handleSave} variant="contained" disabled={updating}>
            {updating ? <CircularProgress size={20} /> : "保存"}
          </Button>
        </DialogActions>
      </Dialog>
    </Template>
  );
};

export default ConfigManagerPage;
